import { groq } from 'next-sanity'
import { gql } from 'graphql-request'

const imageFields = `
...,
"metadata": asset->metadata,
"lqip": asset->metadata.lqip,
"width": asset->metadata.dimensions.width,
"height": asset->metadata.dimensions.height,
"aspect": asset->metadata.dimensions.aspectRatio,
`

const imageFieldsSimple = `
  asset
`

const linkFields = `
  _key,
  label,
  type,
  url,
  to->{
    _type,
    slug,
    description,
    featuredImage {
      ${imageFields}
    },
    store {
      slug {
        current
      }
    }
  },
  withProducts,
  products[] -> {
    _key,
    "slug": store.slug.current,
    "imgUrl": store.previewImageUrl,
  }
`

const linkQuery = `_type == "link" => {
  ${linkFields}
}`

const linkFieldsSimple = `
  _key,
  label,
  type,
  url,
  to->{
    _type,
    slug,
    description,
    featuredImage {
      ${imageFields}
    },
    store {
      slug {
        current
      }
    }
  }
`

const dropdownQuery = `_type == "dropdown" => {
  _key,
  label,
  links[] {
    ${linkQuery}
  }
}`

const expandableMenuQuery = `_type == "expandableMenu" => {
  _key,
  label,
  links[] {
    _type,
    ${linkQuery},
    ${dropdownQuery},
  }
}`

const donationFields = `
  ...,
  image {
    ${imageFields}
  },
  link {
    ${linkFields}
  },
  title,
  subtitle,
  description,
  amount
`

const collectionFields = `
...,
hideText,
"slug": store.slug.current,
"title": select(
  title != null => title,
  store.title != null => store.title,
  store.title
),
photos[] {
  forOption,
  photos[] {
    ${imageFields}
  }
},
cause -> {
  ...,
  ${donationFields}
}
`

export const settingsQuery = groq`*[_type == "settings"][0] {
  ...,
  logo {
    ${imageFieldsSimple}
  },
  announcement {
    enabled,
    message,
    link {
      ${linkFieldsSimple}
    },
    backgroundColor {
      hex,
      rgb
    },
    textColor {
      hex
    }
  },
  headerMenu[] {
    _type,
    ${linkQuery},
    ${dropdownQuery},
    ${expandableMenuQuery}
  },
  recommendedCollection -> {
    _id,
    "slug": store.slug.current,
  },
  headerMobileMenu[] {
    ...,
    ${linkQuery}
  },
  footerMenus[] {
    ...,
    title,
    links[] {
      ${linkFieldsSimple}
    }
  },
  swatches[] {
    _key,
    "color": color.hex,
    name,
  }
}`

export const indexQuery = groq`
*[_type == "post"] | order(date desc, _updatedAt desc) {
  ...
}`

const hotspotFields = `
  ...,
  _type,
  _type == "hotspotText" => {
    content,
  },
  _type == "hotspot" => {
    product -> {
      "slug": store.slug.current,
      "imgUrl": store.previewImageUrl,
      "title": store.title,
    },
  },
  _type == "hotspotLink" => {
    label,
    tag,
    image {
      ${imageFields}
    },
    link -> {
      _type,
      "slug": store.slug.current,
      "title": store.title,
    }
  },
  _type == "hotspotLinkWithProducts" => {
    label,
    link -> {
      _type,
      "slug": store.slug.current,
    },
    products[] -> {
      ...,
      _key,
      "slug": store.slug.current,
      "imgUrl": store.previewImageUrl,
      "title": store.title,
    }
  }
`

const helpModulesQuery = `
  modules[] {
    _type,
    _key,
    _type == "textSection" => {
      ...,
      title,
      body
    },
    _type == "textBlock" => {
      ...,
      body
    },
    _type == "sectionTitle" => {
      ...,
      title
    },
    _type == "faqSection" => {
      ...,
      title,
      items[] {
        ...,
        question,
        answer
      }
    },
    _type == "emailList" => {
      ...,
      title,
      items[] {
        ...,
        label,
        email
      }
    },
    _type == "shippingTable" => {
      ...,
      rows[] {
        method,
        time,
        rate
      }
    },
    _type == "wholesaleForm" => {
      ...,
      title
    },
    _type == "collaborationsForm" => {
      ...,
      title
    },
    _type == "contactForm" => {
      ...,
      title
    },
  }
`

const worldCategoryQuery = `
  category -> {
    ...,
    "slug": slug.current,
    title,
  }
`

const worldModulesQuery = `
  items[] {
    _type,
    _key,
    published,
    hidden,
    _type == "thread" => {
      ...,
      size,
      title,
      author,
      avatar {
        ${imageFields}
      },
      gridImage {
        ${imageFields}
      },
      ${worldCategoryQuery},
      images[] {
        ${imageFields}
      }
    },
    _type == "tv" => {
      ...,
      author,
      avatar {
        ${imageFields}
      },
      ${worldCategoryQuery},
      title,
      url,
      gridImage {
        ${imageFields}
      },
      image {
        ${imageFields}
      }
    },
    _type == "lookbook" => {
      ...,
      ${worldCategoryQuery},
      title,
      description,
      gridImage {
        ${imageFields}
      },
      collection {
        ...,
        "slug": store.slug.current,
        "title": store.title,
      },
      items[] {
        ...,
        hotspots[] {
          ${hotspotFields}
        },
        image {
          ${imageFields}
        }
      }
    },
    _type == "article" => {
      ...,
      ${worldCategoryQuery},
      title,
      gridImage {
        ${imageFields}
      },
      content[] {
        ...,
        _type,
        size,
        _type == "articleText" => {
          ...,
          text
        },
        _type == "articleImage" => {
          ...,
          image {
            ${imageFields}
          },
          caption,
          productLink {
            ...,
            tag,
            image {
              ${imageFields}
            },
            product -> {
              ...,
              _key,
              "slug": store.slug.current,
              "imgUrl": store.previewImageUrl,
              "title": store.title,
            }
          },
          productHotspots[] {
            ...,
            ${hotspotFields}
          }
        }
      }
    },
    _type == "socialVideo" => {
      ...,
      ${worldCategoryQuery},
      size,
      gridImage {
        ${imageFields}
      },
      mediaType,
      sound,
      video {
        ...,
        "url": asset->url
      },
      image {
        ${imageFields}
      },
      handle,
      caption,
      avatar {
        ${imageFields}
      },
      platform,
      products[] -> {
        ...,
        _key,
        "slug": store.slug.current,
        "imgUrl": store.previewImageUrl,
        "title": store.title,
      }
    }
  }
`

const modulesQuery = `
  modules[] {
    _type,
    _key,
    _type == "multiState" => {
      ...,
    },
    _type == "threeUpTag" => {
      ...,
      layout,
      items[] {
        ...,
        hotspots[] {
          ${hotspotFields}
        },
        image {
          asset,
          alt
        }
      }
    },
    _type == "carousel" => {
      ...,
      items[] {
        ...,
        image {
          asset,
          alt
        },
        collection -> {
          title,
          description,
          cause,
          store {
            slug
          }
        }
      }
    },
    _type == "collectionMarquee" => {
      title,
      subtitle,
      "collection": collection-> {
        "slug": store.slug.current
      }
    },
    _type == "featureBlock" => {
      ...,
      layout,
      items[] {
        ...,
        _type == 'featureImageText' => {
          ...,
          mediaType,
          image {
            ${imageFields}
          },
          video {
            ...,
            "url": asset->url
          },
          cards[] {
            _key,
            tag,
            title,
            subtitle,
            image {
              ${imageFields}
            },
            link {
              ${linkFields}
            }
          }
        },
        _type == 'featureImage' => {
          ...,
          image {
            ${imageFields}
          },
          hotspots[] {
            ${hotspotFields}
          },
        },
        _type == 'featureText' => {
          ...,
          title,
          topText,
          bottomText
        }
      }
    },
    _type == "scrollingCarousel" => {
      ...,
      collection -> {
        "slug": store.slug.current
      },
      products[] -> {
        _id,
        "slug": store.slug.current,
        "imgUrl": store.previewImageUrl,
        "title": store.title,
        "price": store.priceRange.minVariantPrice,
      },
      cause -> {
        ...,
        ${donationFields}
      }
    },
    _type == "video" => {
      ...,
      layout,
      title,
      url,
      poster {
        ${imageFields}
      }
    },
    _type == "sectionHeader" => {
      ...,
      title,
      eyebrow,
      link {
        ${linkFields}
      }
    },
    _type == "twoColImageText" => {
      ...,
      title,
      eyebrow,
      text,
      image {
        ${imageFields}
      }
    },
    _type == "donationCarousel" => {
      ...,
      title,
      cards[] -> {
        ${donationFields}
      }
    },
    _type == "donationGrid" => {
      "cards": *[_type == "donationCard"]|order(orderRank) {
        ${donationFields}
      }
    },
    _type == "infoGrid" => {
      ...,
      items[] {
        ...,
        image {
          ${imageFields}
        },
        title,
        subtitle,
        body,
        link {
          ${linkFields}
        }
      }
    },
    _type == "counterHero" => {
      ...,
      heading,
      image {
        ${imageFields}
      }
    },
    _type == "worldBlock" => {
      ...,
      title,
      image {
        ${imageFields}
      },
      link -> {
        "slug": store.slug.current,
        "title": store.title,
      },
      "world": *[_type == "worldPage"][0] {
        ...,
        title,
        slug,
        ${worldModulesQuery}
      }
    },
    _type == "worldCarousel" => {
      "world": *[_type == "worldPage"][0] {
        ...,
        title,
        slug,
        ${worldModulesQuery}
      }
    },
    _type == "sectionHeaderWithBody" => {
      ...,
      anchor,
      title,
      body
    },
    _type == "contentBlock" => {
      ...,
      items[] {
        ...,
        title,
        body,
        image {
          ${imageFields}
        }
      }
    },
    _type == "principlesGrid" => {
      ...,
      title,
      items[] {
        ...,
        title,
        tag,
        text
      }
    },
    _type == 'anchorLinks' => {
      ...,
      links[] {
        ...,
        label,
        anchor
      }
    },
  }
`

export const homeQuery = groq`
  *[_type == "homePage"][0] {
    ...,
    title,
    content,
    slug,
    hero {
      ...,
      heading,
      mediaType,
      image {
        ${imageFields}
      },
      imageMobile {
        ${imageFields}
      },
      video {
        ...,
        "url": asset->url
      },
      videoMobile {
        ...,
        "url": asset->url
      },
    },
    ${modulesQuery}
  }
`

export const collectionPathsQuery = groq`
*[_type == "collection" && defined(store.slug)] {
  "params": { "slug": store.slug.current }
}
`

export const collectionQuery = groq`
*[_type == "collection" && store.slug.current == $slug][0] {
  ...,
  ${collectionFields},
  "products": *[_type == "product" && store.gid in $productIds] {
    ...,
    store {
      gid,
      slug,
    }
  }
}
`

export const productsBySlugQuery = groq`
*[_type == "product" && store.slug.current in $slugs] {
  ...,
  photos[] {
    forOption,
    photos[] {
      ${imageFields}
    }
  },
  store {
    ...,
    variants[]->
  }
}
`

export const productPathsQuery = groq`
*[_type == "product" && defined(store.slug)] {
  "params": { "slug": store.slug.current }
}
`

export const productQuery = groq`
*[_type == "product" && store.slug.current == $slug][0] {
  ...,
  "details": *[_type == "pdpDetails"][0] {
    ...,
    care,
    sizing,
    sustainability[] {
      ...,
      label,
      icon {
        ...,
        "url": asset->url
      }
    },
    factory {
      ...,
      image {
        ${imageFields}
      },
      link {
        ${linkFields}
      }
    },
    sizing {
      ...,
      title,
      body,
      categories[] {
        ...,
        type,
        image {
          ${imageFields}
        },
        description,
        tables[] {
          ...,
        }
      }
    }
  },
  cause -> {
    ...,
    ${donationFields}
  },
  wornByYou {
    image {
      ${imageFields}
    },
    items[] {
      mediaType,
      image {
        ${imageFields}
      },
      video {
        ...,
        "url": asset->url
      },
      handle,
      platform
    },
  },
  photos[] {
    forOption,
    photos[] {
      ${imageFields}
    }
  },
  store {
    ...,
    variants[]->
  }
}
`

export const helpPagePathsQuery = groq`
*[_type == "helpPage" && defined(slug)] {
  "params": { "slug": slug.current }
}
`

export const helpPageQuery = groq`
*[_type == "helpPage" && slug.current == $slug][0] {
  ...,
  "pages": *[_type == "helpPage" && defined(slug)] {
    _id,
    "slug": slug.current,
    title,
    ${helpModulesQuery}
  }
}
`

export const worldPageQuery = groq`
*[_type == "worldPage"][0] {
  ...,
  title,
  slug,
  "categories": *[_type == "worldCategory"] {
    ...,
    "slug": slug.current,
    title,
    image {
      ${imageFields}
    }
  },
  ${worldModulesQuery}
}
`

export const pagePathsQuery = groq`
*[_type == "page" && defined(slug)] {
  "params": { "slug": slug.current }
}
`

export const pageQuery = groq`
*[_type == "page" && slug.current == $slug][0] {
  ...,
  title,
  slug,
  ${modulesQuery}
}
`

export const gqlCartFragment = `
fragment CartFragment on Cart {
  id
  checkoutUrl
  totalQuantity
  buyerIdentity {
    countryCode
    customer {
      id
      email
      firstName
      lastName
      displayName
    }
    email
    phone
  }
  lines(first: $numCartLines) {
    edges {
      node {
        id
        quantity
        attributes {
          key
          value
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          compareAtAmountPerQuantity {
            amount
            currencyCode
          }
        }
        merchandise {
          ... on ProductVariant {
            id
            availableForSale
            compareAtPrice {
              ...MoneyFragment
            }
            price {
              ...MoneyFragment
            }
            requiresShipping
            title
            image {
              ...ImageFragment
            }
            product {
              handle
              title
              id
              preorder: metafield(
                namespace: "custom"
                key: "preorder"
              ) {
                value
              }
              backorder: metafield(
                namespace: "custom"
                key: "backorder"
              ) {
                value
              }
              shippingDate: metafield(
                namespace: "custom"
                key: "shipping_date"
              ) {
                value
              }
              discountAmount: metafield(
                namespace: "custom"
                key: "discount_amount"
              ) {
                value
              }
            }
            selectedOptions {
              name
              value
            }
          }
        }
      }
    }
  }
  cost {
    subtotalAmount {
      ...MoneyFragment
    }
    totalAmount {
      ...MoneyFragment
    }
    totalDutyAmount {
      ...MoneyFragment
    }
    totalTaxAmount {
      ...MoneyFragment
    }
  }
  note
  attributes {
    key
    value
  }
  discountCodes {
    code
    applicable
  }
}

fragment MoneyFragment on MoneyV2 {
  currencyCode
  amount
}
fragment ImageFragment on Image {
  id
  url
  altText
  width
  height
}
`

export const gqlProductFragment = `
id
handle
title
tags
productType
vendor
handle
productType
availableForSale
options(first: 250) {
  id
  name
  values
}
compareAtPriceRange {
  minVariantPrice {
    amount
    currencyCode
  }
  maxVariantPrice {
    amount
    currencyCode
  }
}
priceRange {
  minVariantPrice {
    amount
    currencyCode
  }
  maxVariantPrice {
    amount
    currencyCode
  }
}
preorder: metafield(
  namespace: "custom"
  key: "preorder"
) {
  value
}
backorder: metafield(
  namespace: "custom"
  key: "backorder"
) {
  value
}
shippingDate: metafield(
  namespace: "custom"
  key: "shipping_date"
) {
  value
}
discountAmount: metafield(
  namespace: "custom"
  key: "discount_amount"
) {
  value
}
hideSustainability: metafield(
  namespace: "custom"
  key: "hide_sustainability"
) {
  value
}
images(first: 250) {
  edges {
    node {
      id
      src
      altText
      width
      height
    }
  }
}
variants(first: 250) {
  edges {
    node {
      id
      title
      availableForSale
      compareAtPrice {
        amount
        currencyCode
      }
      priceV2 {
        amount
        currencyCode
      }
      selectedOptions {
        name
        value
      }
      image {
        id
        width
        height
        src
        altText
      }
    }
  }
}
`

export const gqlSearchProductsQuery = gql`
  query SearchProducts($query: String!, $limit: Int = 250) {
    products(query: $query, first: $limit) {
      edges {
        node {
          ${gqlProductFragment}
        }
      }
    }
  }
`

export const gqlProductQuery = gql`
  query Product($slug: String!) {
    product(handle: $slug) {
      ${gqlProductFragment}
    }
  }
`

export const recommendedProductsQuery = gql`
  query productRecommendations($id: ID!) {
    productRecommendations(productId: $id) {
      ${gqlProductFragment}
    }
  }
`

export const gqlCollectionQuery = gql`
query Collection($slug: String!) {
  collection(handle: $slug) {
    id
    handle
    title
    description
    products(first: 250) {
      edges {
        node {
          ${gqlProductFragment}
        }
      }
    }
  }
}
`

export const gqlCollectionHeaderQuery = gql`
  query Collection($slug: String!) {
    collection(handle: $slug) {
      products(first: 6) {
        edges {
          node {
            images(first: 250) {
              edges {
                node {
                  id
                  src
                  altText
                  width
                  height
                }
              }
            }
          }
        }
      }
    }
  }
`

export interface Author {
  name?: string
  picture?: any
}

export interface Post {
  _id: string
  title?: string
  coverImage?: any
  date?: string
  excerpt?: string
  author?: Author
  slug?: string
  content?: any
}

export interface Home {
  title?: string
  content?: any
}

export interface Settings {
  logo?: any
  title?: string
  description?: string
  ogImage?: any
  favicon?: any
  announcement?: {
    enabled: boolean
    message: string
    link: any
  }
  headerMenu?: any[]
  headerMobileMenu?: any[]
  recommendedCollection?: any
  swatches?: {
    _key?: string
    color?: string
    name?: string
  }[]
}
